import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Clock, AlertCircle, Gavel, Timer, Eye } from "lucide-react";
import { formatWithTimezoneLabel, isDeadlinePassed } from "@/lib/timezone";

interface LiveAuction {
  id: string;
  rfp_id: string;
  initial_bid_amount: number;
  invitation_id: string;
  invite_token: string;
  rfp: {
    title: string;
    description: string;
    auction_start: string;
    auction_end: string;
    status: string;
    matter_name: string | null;
    deleted_by_buyer: boolean;
  };
  lowestBid: number;
  myCurrentBid: number;
  isLive: boolean; // true if auction is active, false if upcoming
}

const SupplierLiveAuctions = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState<LiveAuction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchLiveAuctions();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchLiveAuctions = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    try {
      // Get invitations for this user
      const { data: invitations } = await supabase
        .from("supplier_invitations")
        .select("id, invite_token, rfp_id")
        .eq("supplier_email", user.email);

      if (!invitations || invitations.length === 0) {
        setAuctions([]);
        setLoading(false);
        return;
      }

      const invitationIds = invitations.map(inv => inv.id);
      const rfpIds = invitations.map(inv => inv.rfp_id);

      // Get proposals for these invitations
      const { data: proposals } = await supabase
        .from("proposals")
        .select("id, rfp_id, initial_bid_amount, invitation_id")
        .in("invitation_id", invitationIds);

      if (!proposals || proposals.length === 0) {
        setAuctions([]);
        setLoading(false);
        return;
      }

      // Get RFPs that are in auction status OR awaiting auction (for upcoming)
      const { data: rfpData } = await supabase
        .from("rfps")
        .select("id, title, description, auction_start, auction_end, status, matter_name, deleted_by_buyer")
        .in("id", rfpIds)
        .in("status", ["AUCTION_ACTIVE", "AWAITING_AUCTION", "OPEN_FOR_SUBMISSIONS"]);

      // Filter to show:
      // 1. Live auctions (started but not ended)
      // 2. Upcoming auctions (proposal submitted, auction not started yet)
      // Exclude deleted RFPs
      const now = new Date();
      const relevantRfps = (rfpData || []).filter(rfp => {
        if (rfp.deleted_by_buyer) return false;
        
        const auctionStart = new Date(rfp.auction_start);
        const auctionEnd = new Date(rfp.auction_end);
        const hasProposal = proposals.some(p => p.rfp_id === rfp.id);
        
        // Live: auction started and not ended
        const isLive = auctionStart <= now && auctionEnd > now;
        // Upcoming: has proposal and auction hasn't started
        const isUpcoming = hasProposal && auctionStart > now && auctionEnd > now;
        
        return isLive || isUpcoming;
      });

      if (!relevantRfps || relevantRfps.length === 0) {
        setAuctions([]);
        setLoading(false);
        return;
      }

      // For each auction, get bid data
      const auctionData = await Promise.all(
        relevantRfps.map(async (rfp) => {
          const proposal = proposals.find(p => p.rfp_id === rfp.id);
          const invitation = invitations.find(inv => inv.rfp_id === rfp.id);
          
          if (!proposal || !invitation) return null;

          const auctionStart = new Date(rfp.auction_start);
          const auctionEnd = new Date(rfp.auction_end);
          const isLive = auctionStart <= now && auctionEnd > now;

          // Get all bids for this RFP
          const { data: allBids } = await supabase
            .from("bids")
            .select("amount")
            .eq("rfp_id", rfp.id)
            .order("amount", { ascending: true });

          // Get my bids
          const { data: myBids } = await supabase
            .from("bids")
            .select("amount")
            .eq("proposal_id", proposal.id)
            .order("amount", { ascending: true });

          const lowestBid = allBids && allBids.length > 0 ? allBids[0].amount : proposal.initial_bid_amount;
          const myCurrentBid = myBids && myBids.length > 0 ? myBids[0].amount : proposal.initial_bid_amount;

          return {
            id: proposal.id,
            rfp_id: rfp.id,
            initial_bid_amount: proposal.initial_bid_amount,
            invitation_id: proposal.invitation_id,
            invite_token: invitation.invite_token,
            rfp: rfp,
            lowestBid,
            myCurrentBid,
            isLive
          };
        })
      );

      // Sort: live auctions first, then upcoming by start time
      const sortedAuctions = (auctionData.filter(Boolean) as LiveAuction[]).sort((a, b) => {
        if (a.isLive && !b.isLive) return -1;
        if (!a.isLive && b.isLive) return 1;
        return new Date(a.rfp.auction_start).getTime() - new Date(b.rfp.auction_start).getTime();
      });

      setAuctions(sortedAuctions);
    } catch (err) {
      console.error("Error fetching live auctions:", err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const liveAuctions = auctions.filter(a => a.isLive);
  const upcomingAuctions = auctions.filter(a => !a.isLive);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Live Auctions</h1>
          <p className="text-muted-foreground mt-2">Auctions in progress and upcoming</p>
        </div>

        {auctions.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No live or upcoming auctions</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Live Auctions Section */}
            {liveAuctions.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse" />
                  Live Now
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {liveAuctions.map((auction) => (
                    <Card key={auction.id} className="hover:shadow-lg transition-shadow flex flex-col border-red-200">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">
                            {auction.rfp.matter_name || auction.rfp.title}
                          </CardTitle>
                          <Badge className="bg-red-500 animate-pulse flex items-center gap-1">
                            <div className="h-2 w-2 bg-white rounded-full" />
                            Live
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4 flex-1 flex flex-col">
                        <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                          {auction.rfp.description}
                        </p>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 bg-blue-50 rounded-lg text-center">
                            <p className="text-xs text-blue-600">Your Bid</p>
                            <p className="text-lg font-bold text-blue-700">${auction.myCurrentBid.toLocaleString()}</p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg text-center">
                            <p className="text-xs text-green-600">Lowest Bid</p>
                            <p className="text-lg font-bold text-green-700">${auction.lowestBid.toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>Ends: {formatWithTimezoneLabel(auction.rfp.auction_end)}</span>
                          </div>
                        </div>
                        
                        <div className="mt-auto pt-4">
                          <Button
                            onClick={() => navigate(`/rfp/${auction.rfp_id}/bid/${auction.invite_token}`)}
                            className="w-full"
                          >
                            <Gavel className="h-4 w-4 mr-2" />
                            Place Bid
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Auctions Section */}
            {upcomingAuctions.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Timer className="h-5 w-5 text-amber-500" />
                  Upcoming Auctions
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {upcomingAuctions.map((auction) => (
                    <Card key={auction.id} className="hover:shadow-lg transition-shadow flex flex-col border-amber-200">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">
                            {auction.rfp.matter_name || auction.rfp.title}
                          </CardTitle>
                          <Badge variant="outline" className="border-amber-500 text-amber-600 flex items-center gap-1">
                            <Timer className="h-3 w-3" />
                            Upcoming
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4 flex-1 flex flex-col">
                        <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                          {auction.rfp.description}
                        </p>
                        
                        <div className="p-3 bg-amber-50 rounded-lg text-center">
                          <p className="text-xs text-amber-600">Your Initial Bid</p>
                          <p className="text-lg font-bold text-amber-700">${auction.initial_bid_amount.toLocaleString()}</p>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-amber-600">
                            <Clock className="h-4 w-4" />
                            <span>Starts: {formatWithTimezoneLabel(auction.rfp.auction_start)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span>Ends: {formatWithTimezoneLabel(auction.rfp.auction_end)}</span>
                          </div>
                        </div>
                        
                        <div className="mt-auto pt-4">
                          <Button
                            onClick={() => navigate(`/supplier/rfp/${auction.rfp_id}/view/${auction.invite_token}`)}
                            variant="outline"
                            className="w-full"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Proposal
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierLiveAuctions;
